import { Component, DestroyRef, inject, model, ModelSignal } from '@angular/core';
import { ButtonComponent } from '@kinomap-dev/ng-ui-lib/buttons';
import { CheckboxComponent, SelectComponent } from '@kinomap-dev/ng-ui-lib/forms';
import { ModeSwitchComponent } from '@kinomap-dev/ng-ui-lib/elements';
import { LoginService } from 'app/core/auth/login.service';
import { Dialog } from '@angular/cdk/dialog';
import { ConfirmModalComponent } from 'app/shared/ui/modals/confirm-modal/confirm-modal.component';
import { EmailSendModalComponent } from 'app/shared/ui/modals/email-send-modal/email-send-modal.component';
import { HttpErrorResponse } from '@angular/common/http';
import { EmailRequiredModalComponent } from 'app/shared/ui/modals/email-required-modal/email-required-modal.component';
import {
  LeaderboardMetricId,
  LeaderboardMetricOption,
} from 'app/features/contest/models/contest-template.models';
import { ContestApi } from 'app/features/contest/data-access/api/contest-api.types';
import { ContestStore } from 'app/features/contest/data-access/contest-store';
import { ContestService } from 'app/features/contest/data-access/contest.service';
import { ToolbarShellComponent } from 'app/shared/ui/toolbar-shell/toolbar-shell.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  imports: [
    ModeSwitchComponent,
    SelectComponent,
    ButtonComponent,
    ToolbarShellComponent,
    CheckboxComponent,
  ],
  selector: 'app-toolbar-contest',
  styleUrl: './toolbar-contest.component.scss',
  templateUrl: './toolbar-contest.component.html',
})
export class ToolbarContestComponent {
  readonly editionModeEnabled: ModelSignal<boolean> = model<boolean>(false);
  readonly showRankingBlock: ModelSignal<boolean> = model<boolean>(true);

  pprTimes: number[] = [
    3, 5, 10, 15, 20, 30, 45, 60, 120, 180, 240, 300, 360, 420, 480, 540, 600, 900, 1200,
  ];

  private api: ContestApi = inject(ContestApi);
  readonly store: ContestStore = inject(ContestStore);
  readonly contestService: ContestService = inject(ContestService);
  public loginService: LoginService = inject(LoginService);
  private dialog: Dialog = inject(Dialog);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  exportLoading: boolean = false;
  resetLoading: boolean = false;

  openResetConfirmModal(): void {
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmation',
        message: 'Êtes-vous sûr de vouloir réinitialiser les données du concours ?',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        onConfirm: () => this.reset(),
      },
    });
  }

  openSendEmailModal(): void {
    this.dialog.open(EmailSendModalComponent);
  }

  openLogoutModal(): void {
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmation',
        message: 'Êtes-vous sûr de vouloir vous déconnecter ?',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        onConfirm: () => this.loginService.logout(),
      },
    });
  }

  enableEditionMode(e: Event): void {
    const enabled: boolean = (e.target as HTMLInputElement).checked;

    this.editionModeEnabled.set(enabled);
  }

  genderChange(event: string): void {
    this.contestService.setTargetGender(event);
  }

  pprFilterChange(event: string): void {
    const ppr: number = parseInt(event, 10);
    if (this.pprTimes.includes(ppr)) this.contestService.setPprFilter(ppr);
  }

  selectedMetricChange(event: string): void {
    if (this.isLeaderboardMetricId(event)) this.contestService.selectMetric(event);
  }

  reset(): void {
    const username: string | null = this.store.username();
    const hashId: string | null = this.store.hashid();
    if (!username || !hashId) return;

    this.resetLoading = true;
    this.api
      .resetData(username, hashId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.resetLoading = false;
          void this.contestService.refreshData();
        },
        error: (err: Error) => {
          console.error(err);
          this.resetLoading = false;
        },
      });
  }

  openRequiredEmailModal(): void {
    this.dialog.open(EmailRequiredModalComponent, {
      data: {
        onConfirm: (email: string) => this.export(email),
      },
    });
  }

  export(email: string): void {
    const username: string | null = this.store.username();
    const hashId: string | null = this.store.hashid();
    if (!username || !hashId) return;

    this.exportLoading = true;
    this.api
      .exportData(username, hashId, this.store.pprFilter(), email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.exportLoading = false;
          this.openSendEmailModal();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 200) {
            this.openSendEmailModal();
          } else {
            console.error('Error exporting data:', err.message);
          }
          this.exportLoading = false;
        },
      });
  }

  private isLeaderboardMetricId(value: string): value is LeaderboardMetricId {
    return this.store
      .metricOptions()
      .some((option: LeaderboardMetricOption) => option.id === value);
  }
}
